import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  companyStats,
  companyTimeline,
  coreValues,
  teamMembers,
  partnerLogos,
} from '../../mocks/about';

function AnimatedNumber({ target, suffix = '' }: { target: string; suffix?: string }) {
  const [count, setCount] = useState(0);
  const numericPart = parseFloat(target.replace(/[^0-9.]/g, ''));
  const hasPlus = target.includes('+');

  useEffect(() => {
    let start = 0;
    const duration = 2000;
    const increment = numericPart / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= numericPart) {
        setCount(numericPart);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start * 10) / 10);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [numericPart]);

  const display = Number.isInteger(numericPart) ? Math.floor(count) : count.toFixed(1);
  return (
    <span>
      {display}
      {hasPlus && '+'}
      {suffix}
    </span>
  );
}

export default function AboutPage() {
  const [activeTimeline, setActiveTimeline] = useState(0);

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src="https://public.readdy.ai/ai/img_res/8d0e19d0-7bfb-4b2d-af00-cea6f100e1c2.png"
                alt="봉구 투어 로고"
                className="h-12 w-auto"
              />
              <span className="text-2xl font-bold text-gray-900">봉구 투어</span>
            </Link>
            <div className="flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                홈
              </Link>
              <Link to="/#tours" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                투어 상품
              </Link>
              <Link to="/about" className="text-sm font-medium text-[#00BCD4] cursor-pointer whitespace-nowrap">
                회사 소개
              </Link>
              <Link to="/faq" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                자주 묻는 질문
              </Link>
              <Link to="/#contact" className="px-6 py-2.5 bg-[#1A237E] text-white text-sm font-semibold rounded-full hover:bg-[#0D47A1] transition-colors cursor-pointer whitespace-nowrap">
                문의하기
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 overflow-hidden">
        <div className="relative w-full h-[520px]">
          <img
            src="https://public.readdy.ai/ai/img_res/fd3018280080fc3b0d1c34f297eb77ff.jpg"
            alt="세부 전경"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white w-full max-w-3xl px-6">
              <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/15 backdrop-blur-sm rounded-full mb-6">
                <i className="ri-team-line text-[#00BCD4]"></i>
                <span className="text-sm font-semibold text-white">ABOUT US</span>
              </div>
              <h1 className="text-5xl font-bold mb-5 leading-tight">
                세부의 아름다움을<br />당신에게 전합니다
              </h1>
              <p className="text-lg text-white/85 max-w-xl mx-auto leading-relaxed">
                2024년부터 필리핀 세부 현지에서 한국인 여행객을 위한<br />
                안전하고 특별한 투어를 만들어가는 봉구 투어입니다
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="px-6 -mt-16 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl p-8 grid grid-cols-4 gap-6">
            {companyStats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="w-12 h-12 bg-[#E0F7FA] rounded-xl flex items-center justify-center mx-auto mb-3">
                  <i className={`${stat.icon} text-[#00BCD4] text-2xl`}></i>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  <AnimatedNumber target={stat.number} />
                </div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E0F7FA] rounded-full mb-6">
                <i className="ri-focus-3-line text-[#00BCD4]"></i>
                <span className="text-sm font-semibold text-[#00BCD4]">OUR MISSION</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                모든 여행자에게<br />
                <span className="text-[#00BCD4]">잊을 수 없는 경험</span>을<br />
                선사합니다
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                봉구 투어는 단순한 관광을 넘어, 필리핀 세부의 진정한 매력을 느낄 수 있는 여행을 만듭니다. 젊고 열정적인 팀이 현지 네트워크를 바탕으로, 안전하면서도 특별한 투어를 제공합니다.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                한국어 가이드와 함께하는 맞춤형 일정, 합리적인 가격, 그리고 세심한 고객 케어까지. 여행의 시작부터 끝까지 봉구 투어가 함께합니다.
              </p>
              <div className="flex gap-4">
                <Link
                  to="/#tours"
                  className="px-7 py-3 bg-[#00BCD4] text-white font-semibold rounded-full hover:bg-[#00ACC1] transition-colors inline-flex items-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-compass-3-line"></i>
                  투어 둘러보기
                </Link>
                <Link
                  to="/#contact"
                  className="px-7 py-3 bg-white text-gray-700 font-semibold rounded-full border border-gray-200 hover:border-[#00BCD4] hover:text-[#00BCD4] transition-colors inline-flex items-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-chat-3-line"></i>
                  상담 신청
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=happy%20korean%20tourists%20enjoying%20island%20hopping%20tour%20on%20traditional%20filipino%20bangka%20boat%20crystal%20clear%20turquoise%20water%20cebu%20philippines%20group%20of%20friends%20smiling%20having%20fun%20tropical%20paradise%20warm%20sunny%20day%20simple%20clean%20background&width=600&height=500&seq=about-mission&orientation=portrait"
                  alt="봉구 투어 여행객"
                  className="w-full h-[480px] object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#1A237E] rounded-2xl p-5 text-white shadow-xl">
                <div className="text-3xl font-bold mb-1">Since 2024</div>
                <div className="text-sm text-white/70">세부 현지 직영 운영</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E0F7FA] rounded-full mb-5">
              <i className="ri-sparkling-line text-[#00BCD4]"></i>
              <span className="text-sm font-semibold text-[#00BCD4]">CORE VALUES</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">봉구 투어의 핵심 가치</h2>
            <p className="text-gray-600 max-w-xl mx-auto">신생 여행사의 열정과 혁신으로 만드는 우리의 약속입니다</p>
          </div>

          <div className="grid grid-cols-4 gap-6">
            {coreValues.map((value, idx) => (
              <div
                key={idx}
                className="rounded-2xl p-7 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl cursor-default border border-gray-100 bg-white group"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: value.color }}
                >
                  <i className={`${value.icon} text-2xl`} style={{ color: value.iconColor }}></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">{value.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E0F7FA] rounded-full mb-5">
              <i className="ri-time-line text-[#00BCD4]"></i>
              <span className="text-sm font-semibold text-[#00BCD4]">OUR JOURNEY</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">봉구 투어의 발자취</h2>
            <p className="text-gray-600">2024년부터 지금까지, 빠른 성장의 여정을 함께합니다</p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Horizontal line */}
            <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-200"></div>
            <div
              className="absolute top-6 left-0 h-0.5 bg-[#00BCD4] transition-all duration-500"
              style={{ width: `${((activeTimeline + 1) / companyTimeline.length) * 100}%` }}
            ></div>

            {/* Year dots */}
            <div className="flex justify-between mb-12 relative">
              {companyTimeline.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTimeline(idx)}
                  className="flex flex-col items-center cursor-pointer group"
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                      idx <= activeTimeline
                        ? 'bg-[#00BCD4] text-white shadow-lg shadow-[#00BCD4]/30'
                        : 'bg-white text-gray-400 border-2 border-gray-200 group-hover:border-[#00BCD4] group-hover:text-[#00BCD4]'
                    }`}
                  >
                    <span className="text-xs font-bold">{item.year}</span>
                  </div>
                  <span
                    className={`mt-3 text-xs font-semibold transition-colors ${
                      idx === activeTimeline ? 'text-[#00BCD4]' : 'text-gray-400'
                    }`}
                  >
                    {item.year}
                  </span>
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="bg-white rounded-3xl p-10 shadow-sm border border-gray-100 transition-all duration-500">
              <div className="flex items-start gap-10">
                <div className="flex-1">
                  <div className="text-sm font-bold text-[#00BCD4] mb-2">{companyTimeline[activeTimeline].year}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{companyTimeline[activeTimeline].title}</h3>
                  <p className="text-gray-600 leading-relaxed text-base">{companyTimeline[activeTimeline].description}</p>
                </div>
                <div className="w-80 h-48 rounded-2xl overflow-hidden flex-shrink-0">
                  <img
                    src={`https://readdy.ai/api/search-image?query=cebu%20philippines%20tourism%20travel%20agency%20milestone%20achievement%20$%7BcompanyTimeline%5BactiveTimeline%5D.year%7D%20tropical%20island%20tour%20boat%20ocean%20beach%20happy%20tourists%20warm%20golden%20light%20simple%20clean%20background&width=400&height=240&seq=timeline-${activeTimeline}&orientation=landscape`}
                    alt={companyTimeline[activeTimeline].title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E0F7FA] rounded-full mb-5">
              <i className="ri-team-line text-[#00BCD4]"></i>
              <span className="text-sm font-semibold text-[#00BCD4]">OUR TEAM</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">열정 가득한 전문가들</h2>
            <p className="text-gray-600 max-w-xl mx-auto">세부를 사랑하는 전문 가이드와 스태프가 최고의 여행을 만들어드립니다</p>
          </div>

          <div className="grid grid-cols-4 gap-6">
            {teamMembers.map((member, idx) => (
              <div
                key={idx}
                className="group rounded-2xl overflow-hidden bg-[#FAFAF8] border border-gray-100 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl cursor-default"
              >
                <div className="w-full h-64 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-base font-bold text-gray-900 mb-1">{member.name}</h4>
                  <div className="text-sm text-[#00BCD4] font-medium mb-3">{member.role}</div>
                  <p className="text-xs text-gray-500 leading-relaxed">{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h3 className="text-xl font-bold text-gray-900 mb-2">신뢰할 수 있는 파트너</h3>
            <p className="text-sm text-gray-500">공인 기관과 함께하는 안전한 투어</p>
          </div>
          <div className="flex items-center justify-center gap-10">
            {partnerLogos.map((partner, idx) => (
              <div
                key={idx}
                className="flex flex-col items-center gap-2 text-gray-400 hover:text-[#00BCD4] transition-colors cursor-default"
              >
                <div className="w-16 h-16 bg-white rounded-2xl border border-gray-100 flex items-center justify-center shadow-sm">
                  <i className={`${partner.icon} text-2xl`}></i>
                </div>
                <span className="text-xs font-medium">{partner.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=breathtaking%20sunset%20over%20calm%20turquoise%20ocean%20water%20with%20silhouette%20of%20traditional%20filipino%20bangka%20boat%20cebu%20philippines%20golden%20orange%20pink%20sky%20reflection%20on%20water%20romantic%20peaceful%20atmosphere%20simple%20clean%20background&width=1000&height=400&seq=about-cta&orientation=landscape"
              alt="세부 석양"
              className="w-full h-[300px] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A237E]/85 via-[#1A237E]/70 to-[#00BCD4]/50"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <h3 className="text-3xl font-bold mb-3">봉구 투어와 함께 떠나볼까요?</h3>
                <p className="text-white/80 mb-8 text-base">지금 바로 상담을 시작하세요. 맞춤형 일정을 만들어드립니다.</p>
                <div className="flex items-center justify-center gap-4">
                  <Link
                    to="/#contact"
                    className="px-7 py-3 bg-white text-[#1A237E] font-semibold rounded-full hover:bg-gray-100 transition-colors inline-flex items-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-chat-3-line"></i>
                    무료 상담 신청
                  </Link>
                  <Link
                    to="/#tours"
                    className="px-7 py-3 bg-[#00BCD4] text-white font-semibold rounded-full hover:bg-[#00ACC1] transition-colors inline-flex items-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-compass-3-line"></i>
                    투어 상품 보기
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl p-12">
            <div className="grid grid-cols-5 gap-12 mb-12">
              <div className="col-span-1">
                <Link to="/" className="flex items-center gap-2 mb-4 cursor-pointer">
                  <img
                    src="https://public.readdy.ai/ai/img_res/8d0e19d0-7bfb-4b2d-af00-cea6f100e1c2.png"
                    alt="봉구 투어"
                    className="h-10 w-auto"
                  />
                  <span className="text-xl font-bold text-gray-900">봉구 투어</span>
                </Link>
                <p className="text-sm text-gray-600 leading-relaxed">
                  필리핀 세부 현지 여행 전문<br />안전한 투어 보장
                </p>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4"><a href="/#tours">TOURS</a></h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/tour/oslob-whale-shark" className="hover:text-[#00BCD4] transition-colors cursor-pointer">고래상어 투어</Link></li>
                  <li><Link to="/tour/kawasan-falls" className="hover:text-[#00BCD4] transition-colors cursor-pointer">카와산 폭포</Link></li>
                  <li><Link to="/tour/bohol-day-tour" className="hover:text-[#00BCD4] transition-colors cursor-pointer">보홀 당일 투어</Link></li>
                  <li><Link to="/tour/sunset-cruise" className="hover:text-[#00BCD4] transition-colors cursor-pointer">선셋 크루즈</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4">COMPANY</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/about" className="text-[#00BCD4] font-medium cursor-pointer">회사 소개</Link></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">가이드 소개</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">파트너십</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">채용 정보</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4">SUPPORT</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/faq" className="hover:text-[#00BCD4] transition-colors cursor-pointer">자주 묻는 질문</Link></li>
                  <li><Link to="/#contact" className="hover:text-[#00BCD4] transition-colors cursor-pointer">예약 문의</Link></li>
                  <li><Link to="/refund-policy" className="hover:text-[#00BCD4] transition-colors cursor-pointer">환불 정책</Link></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">이용 약관</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4">FOLLOW US</h4>
                <div className="flex gap-3">
                  <a href="#" className="w-10 h-10 bg-[#FFE500] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
                    <i className="ri-kakao-talk-fill text-[#3C1E1E] text-xl"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#515BD4] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
                    <i className="ri-instagram-line text-white text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-8 flex items-center justify-between">
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <span>&copy; 2025 봉구 투어. All rights reserved.</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="nofollow noopener noreferrer" className="hover:text-[#00BCD4] transition-colors cursor-pointer">Website Builder</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
